
import React, { useEffect, useMemo, useState } from "react"
import { checkTerms } from "../../../services/euipoApi"
import DatabaseList from "./DatabaseList"
import ManualList from "./ManualList"
import DirectoryReview from "./DirectoryReview"
import SectionHeader from "../../ui/SectionHeader"
import { FiPackage } from "react-icons/fi"
import PriorApplicationImportButton from "../../ui/PriorApplicationImportButton"
import { useResetListener } from "../../../hooks/useResetListener"

interface GoodsEntry {
  conceptId?: string
  classNumber?: number
  text: string
}

interface GoodsSectionProps {
  goodsAndServicesId?: string | null
  onChange: (data: GoodsEntry[] | null) => void
  onFeesChange?: (data: GoodsEntry[] | null) => void
  onIdChange?: (id: string | null) => void
  initialData?: GoodsEntry[] | null
  disabled?: boolean
  currentApplicationId?: string | null
  ownerUserId?: string | null
}

type EntryMode = "database" | "manual"

export default function GoodsSection({
  onChange,
  onFeesChange,
  onIdChange,
  initialData,
  disabled = false,
  currentApplicationId,
  ownerUserId,
}: GoodsSectionProps) {
  const [activeButton, setActiveButton] = useState<EntryMode | null>(null)
  const [lastMode, setLastMode] = useState<EntryMode>("database")
  const [terms, setTerms] = useState<GoodsEntry[]>([])
  const [savedDirectory, setSavedDirectory] = useState(false)

  const [validation, setValidation] = useState<Record<string, boolean | null>>({})
  const [validationLoading, setValidationLoading] = useState(false)
  const [validationError, setValidationError] = useState(false)

  const validationInput = useMemo(
    () =>
      JSON.stringify(
        terms
          .filter((term) => term.classNumber != null)
          .map((term) => ({
            classNumber: term.classNumber,
            text: term.text,
          }))
      ),
    [terms]
  )

  // Automatically validate terms after every change.
  useEffect(() => {
    let cancelled = false

    const current = JSON.parse(validationInput) as {
      classNumber: number
      text: string
    }[]

    setValidation({})
    setValidationError(false)

    if (current.length === 0) {
      setValidationLoading(false)
      return
    }

    setValidationLoading(true)

    const timer = window.setTimeout(async () => {
      const groups = new Map<number, string[]>()

      current.forEach((term) => {
        groups.set(term.classNumber, [
          ...(groups.get(term.classNumber) ?? []),
          term.text,
        ])
      })

      const classes = Array.from(
        groups,
        ([classNumber, groupedTerms]) => ({
          classNumber,
          terms: groupedTerms,
        })
      )

      try {
        const result = await checkTerms(classes)

        if (cancelled) return

        const next: Record<string, boolean | null> = {}

        for (const cls of result.goodsAndServices ?? []) {
          for (const term of cls.terms ?? []) {
            next[`${term.text}|${cls.classNumber}`] =
              term.harmonized ?? null
          }
        }

        setValidation(next)
      } catch (error) {
        if (cancelled) return

        console.error("Fehler bei der EUIPO-Validierung:", error)
        setValidationError(true)
      } finally {
        if (!cancelled) {
          setValidationLoading(false)
        }
      }
    }, 400)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [validationInput])

  // Restore existing application data.
  useEffect(() => {
    if (initialData && initialData.length > 0) {
      setTerms(initialData)
      setSavedDirectory(true)
      setActiveButton(null)

      sessionStorage.setItem(
        "checkedTerms",
        JSON.stringify(initialData)
      )
      sessionStorage.setItem("savedDirectory", "true")

      window.dispatchEvent(
        new CustomEvent("sectionComplete", {
          detail: {
            section: "goods",
            complete: true,
          },
        })
      )
    }
  }, [initialData])

  useEffect(() => {
    onFeesChange?.(terms.length > 0 ? terms : null)
  }, [terms, onFeesChange])

  // Only pass confirmed terms to the application.
  useEffect(() => {
    onChange(terms.length > 0 && savedDirectory ? terms : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terms, savedDirectory])

  // React to the Save directory event.
  useEffect(() => {
    const handler = () => {
      const isSaved =
        sessionStorage.getItem("savedDirectory") === "true"

      setSavedDirectory(isSaved)

      if (isSaved) {
        setActiveButton(null)
      }
    }

    window.addEventListener("savedDirectoryChanged", handler)

    return () => {
      window.removeEventListener("savedDirectoryChanged", handler)
    }
  }, [])

  useResetListener(() => {
    setTerms([])
    setSavedDirectory(false)
    setActiveButton(null)

    sessionStorage.removeItem("checkedTerms")
    sessionStorage.removeItem("savedDirectory")

    onIdChange?.(null)

    window.dispatchEvent(
      new CustomEvent("sectionComplete", {
        detail: {
          section: "goods",
          complete: false,
        },
      })
    )
  })

  const handleImportGoods = (importedTerms: GoodsEntry[]) => {
    setTerms(importedTerms)
    setSavedDirectory(importedTerms.length > 0)
    setActiveButton(null)

    onChange(importedTerms.length > 0 ? importedTerms : null)

    sessionStorage.setItem(
      "checkedTerms",
      JSON.stringify(importedTerms)
    )
    sessionStorage.setItem(
      "savedDirectory",
      importedTerms.length > 0 ? "true" : "false"
    )

    window.dispatchEvent(new Event("savedDirectoryChanged"))

    window.dispatchEvent(
      new CustomEvent("sectionComplete", {
        detail: {
          section: "goods",
          complete: importedTerms.length > 0,
        },
      })
    )
  }

  function selectMode(mode: EntryMode) {
    if (disabled) return

    setLastMode(mode)
    setActiveButton(mode)

    if (savedDirectory) {
      setSavedDirectory(false)
      sessionStorage.setItem("savedDirectory", "false")

      window.dispatchEvent(
        new Event("savedDirectoryChanged")
      )
    }
  }

  function handleEdit() {
    if (disabled) return
    selectMode(lastMode)
  }

  return (
    <section className="border-b border-gray-200 pb-6">
      <SectionHeader
        icon={<FiPackage size={20} />}
        title="Goods and Services"
        subtitle="Specify the goods and services that the trademark is intended to cover."
        action={
          <PriorApplicationImportButton
            source="trademark"
            target="goods"
            currentApplicationId={currentApplicationId}
            ownerUserId={ownerUserId}
            disabled={disabled}
            hasCurrentData={terms.length > 0}
            onImport={handleImportGoods}
          />
        }
      />

      {!savedDirectory && !disabled && (
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {([
            {
              id: "database" as const,
              icon: "/database.svg",
              title: "Harmonized Database",
              description: "Search and select EUIPO terms",
            },
            {
              id: "manual" as const,
              icon: "/manual.svg",
              title: "Manual entry",
              description: "Enter or paste your own terms",
            },
          ]).map(({ id, icon, title, description }) => (
            <button
              key={id}
              type="button"
              onClick={() => selectMode(id)}
              aria-pressed={activeButton === id}
              className={`flex min-h-24 items-center gap-4 rounded-xl border p-4 text-left shadow-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-700 ${
                activeButton === id
                  ? "border-blue-700 bg-blue-50 ring-1 ring-blue-700"
                  : "border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50"
              }`}
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-blue-50">
                <img
                  src={icon}
                  alt=""
                  className="h-7 w-7 object-contain"
                />
              </span>

              <span className="min-w-0">
                <span className="block text-sm font-semibold text-blue-900">
                  {title}
                </span>
                <span className="mt-1 block text-xs leading-5 text-slate-600">
                  {description}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}

      {!savedDirectory && activeButton && !disabled && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-5">
          {activeButton === "database" ? (
            <DatabaseList
              terms={terms}
              setTerms={setTerms}
            />
          ) : (
            <ManualList
              terms={terms}
              setTerms={setTerms}
            />
          )}
        </div>
      )}

      {!savedDirectory &&
        activeButton &&
        terms.length > 0 &&
        !disabled && (
          <div className="mt-4 rounded-xl border border-slate-200 bg-white shadow-sm">
            <DirectoryReview
              terms={terms}
              setTerms={setTerms}
              onBack={() => {}}
              validation={validation}
              checking={validationLoading}
              validationError={validationError}
              liveMode
            />
          </div>
        )}

      {savedDirectory && terms.length > 0 && (
        <div className="mt-5 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
          <DirectoryReview
            terms={terms}
            setTerms={setTerms}
            initialSaved={true}
            disabled={disabled}
            validation={validation}
            checking={validationLoading}
            validationError={validationError}
            onBack={handleEdit}
          />
        </div>
      )}
    </section>
  )
}
